import type { GuideId, Guide, GuideCopy } from '../content';

const secretHitlerRules = {
  label: 'Secret Hitler 官方规则',
  url: 'https://www.secrethitler.com/assets/Secret_Hitler_Rules.pdf',
};
const secretHitlerOnline = { label: 'Secret Hitler.io 在线版', url: 'https://secrethitler.io/' };
const werewolfRules = {
  label: 'Bézier Games 出版的 Ultimate Werewolf（终极狼人）',
  url: 'https://beziergames.com/products/ultimate-werewolf',
};
const oneNight = {
  label: 'Bézier Games 出版的 One Night Ultimate Werewolf（一夜终极狼人）',
  url: 'https://beziergames.com/products/one-night-ultimate-werewolf',
};
const browserWerewolf = { label: 'BoardGameJoin 上的狼人杀', url: 'https://boardgamejoin.com/en/games/werewolf/' };
const amongUs = { label: 'Innersloth 开发的 Among Us', url: 'https://www.innersloth.com/games/among-us/' };

export const guides: Record<GuideId, Guide> = {
  social_deduction_games_online: {
    slug: 'social-deduction-games-online',
    breadcrumb: '在线社交推理游戏',
    title: '在线社交推理游戏：5–10 人好友局怎么选',
    lead: '阿瓦隆、Secret Hitler、狼人杀都有浏览器版，适合和朋友在线玩社交推理。Among Us 需安装，侧重移动线索。',
    preview: true,
    table: {
      caption: '为下一次朋友聚会挑选游戏',
      columns: ['游戏与人数', '适合的玩法偏好', '实际游玩的区别'],
      rows: [
        {
          title: '阿瓦隆 · 5–10 位真人，或单人／与朋友和机器人一起玩',
          cells: [
            '讨论、公开投票与秘密破坏任务。',
            '普通真人对局免费，供 5–10 位拥有账号的玩家参与。你可以单人游玩，也可以邀请朋友，让机器人补齐空位；在设置中选择当前可用的对局人数和模式。任务期间无人被淘汰。',
          ],
        },
        {
          title: 'Secret Hitler · 5–10 人',
          cells: [
            '选出政府，并讨论政策牌究竟发生了什么。',
            'Secret Hitler.io 提供浏览器版，支持账号和私人对局。处决可能让玩家退出对局。',
          ],
          source: secretHitlerOnline,
        },
        {
          title: '狼人杀 · BoardGameJoin 上支持 4–30 人',
          cells: [
            '经典昼夜循环，围绕指控与特殊角色展开推理。',
            '这个浏览器版本也适合 5–10 人好友局。它会处理夜间行动和投票，但需要房主推进阶段；玩家可能被淘汰。',
          ],
          source: browserWerewolf,
        },
        {
          title: 'Among Us · 4–15 人',
          cells: [
            '四处移动、完成任务并建立不在场证明。',
            '需要安装游戏，不是浏览器游戏。开局前请确认所有人的游戏平台和聊天设置。',
          ],
          source: amongUs,
        },
      ],
    },
    sections: [
      {
        title: '浏览器就能玩，还是需要下载？',
        paragraphs: [
          '本站阿瓦隆在浏览器中游玩，无需下载客户端。普通真人对局免费，适合 5–10 人；每位朋友需要自己的账号和设备，登录后通过房间链接加入。上表也列出了 Secret Hitler 与狼人杀的浏览器版本，Among Us 则需要安装游戏。',
          '想先试一局，可以单人玩或邀请朋友，让机器人补齐空位。AI 对局需要账号，模式与人数以设置中的当前可用情况为准，部分人数需要 Premium。机器人讨论支持英语、俄语、繁体中文、西班牙语和越南语；页面是简体中文，不代表机器人能用简体中文讨论。',
        ],
      },
      {
        title: '阿瓦隆与 Secret Hitler：任务线索还是政策牌？',
        paragraphs: [
          '如果你们喜欢反复检验对同一批玩家的信任，可以选择阿瓦隆。所有人都对提议的队伍投票，但只有队员秘密提交任务牌。任务失败会将怀疑范围缩小到队员；任务成功的队伍中也可能有选择不破坏任务的坏人。',
          '如果你们喜欢听取关于私下传牌的相互矛盾的说法，可以选择 Secret Hitler。总统和总理从抽到的牌中选择一项政策，因此政策结果与两人的解释都很重要。政策牌堆带来的不确定性，是阿瓦隆基础版任务牌所没有的。',
        ],
        source: secretHitlerRules,
      },
      {
        title: '狼人杀与 Among Us：夜间行动还是移动线索？',
        paragraphs: [
          '经典狼人杀和 Mafia（杀人游戏）的核心是淘汰嫌疑人并解读夜间行动。远程游玩时，可以选择能处理所用规则的线上版本，或事先约定由谁主持。上面的 BoardGameJoin 版本会自动处理秘密行动，但阶段切换仍需房主操作。',
          'Among Us 在讨论之间加入了行动环节：船员执行任务，内鬼进行破坏和击杀。会议会将大家目击的情况转化为指控。它适合希望在争论之间也有事可做的朋友；阿瓦隆则让大家集中讨论共同的任务决策。',
        ],
        source: amongUs,
      },
      {
        title: '不想提前出局：阿瓦隆与一夜狼人怎么选？',
        paragraphs: [
          '阿瓦隆的任务阶段让每位玩家始终参与讨论和每次组队投票。未被选入任务队伍只是暂时的：你仍然能投票、观察结果，并在下次被选中。刺杀是终局决策，不会让玩家接连被淘汰、只能旁观剩下的游戏。',
          '并非所有狼人游戏都有淘汰机制。One Night Ultimate Werewolf（一夜终极狼人）是一款独立的 3–10 人游戏，只有一夜、一次讨论，没有提前淘汰。出版商提供的浏览器应用是旁白辅助工具，单靠它并不能组成完整的远程多人游戏房间。',
        ],
        source: oneNight,
      },
      {
        title: '5–10 人好友局：先约好语言和聊天方式',
        paragraphs: [
          '发送游戏链接前，先确认实际参加的人数、选择共同语言，并决定如何交流。在本站玩阿瓦隆，每位朋友都需要自己的账号和设备。先使用熟悉的角色，可选角色和扩展留到之后再尝试。',
          '如果准备好的人少于五位，你可以单人游玩，或邀请朋友，让机器人补齐空位。若想玩普通真人对局，可以另约时间或招募玩家。玩家和组队公告板帮助大家相互联系，但不会自动填满房间，也不保证立刻匹配到对局。',
        ],
      },
      {
        title: '凑不齐人时，用机器人练习任务投票',
        paragraphs: [
          '体验任务流程不必先凑齐一整组人。单人机器人对局能让你提议队伍、投票并查看任务结果；带引导的“首次游戏”可以帮助你入门。你也可以邀请朋友，让机器人补齐空位，并在设置中选择对局人数和模式。',
          '用机器人对局理解梅林掌握的信息和最终刺杀如何与任务相衔接，再把这些决策教给真人朋友。大厅的 AI 设置会显示当前可用的模式和对局人数。',
        ],
      },
    ],
  },
  games_like_secret_hitler: {
    slug: 'games-like-secret-hitler',
    breadcrumb: '类似 Secret Hitler 的游戏',
    title: '类似 Secret Hitler 的游戏：阿瓦隆有何不同？',
    lead: '喜欢 Secret Hitler（秘密希特勒）的隐藏阵营和投票，可试阿瓦隆。它靠任务结果推理，不抽政策牌，任务期间不淘汰玩家。',
    table: {
      caption: 'Secret Hitler 与阿瓦隆：决策方式有哪些变化',
      columns: ['决策环节', 'Secret Hitler', '阿瓦隆'],
      rows: [
        {
          title: '批准的对象',
          cells: ['总统及其提名的总理。', '任务队伍，人数取决于当前任务和玩家总人数。'],
        },
        {
          title: '秘密行动',
          cells: ['当选的两人抽取并弃置政策牌。', '每位被选中的玩家秘密提交一张任务牌。'],
        },
        {
          title: '坏结果可能由什么造成？',
          cells: ['政策选择，以及抽到的牌。', '基础版中，至少一位邪恶阵营队员选择了“失败”。'],
        },
        {
          title: '参与方式',
          cells: ['处决可能让玩家在游戏结束前退出。', '任务期间，每个人始终参与讨论和投票。'],
        },
        {
          title: '特殊的终局条件',
          cells: [
            '通过三项法西斯政策后，选希特勒为总理可能直接结束游戏。',
            '三次任务成功后，邪恶阵营仍可通过认出梅林而获胜。',
          ],
        },
      ],
    },
    sections: [
      {
        title: '从 Secret Hitler 转到阿瓦隆，线上怎么开局？',
        paragraphs: [
          '本站阿瓦隆无需下载客户端。凑齐 5–10 位真人后，每人用自己的账号和设备登录；房主在大厅创建房间，再把链接发给朋友。普通真人对局免费，先用简单角色配置体验组队投票，避免第一次就引入太多额外信息。',
          '朋友还没到齐，可以在“与 AI 对战”中选择“单人”或“和好友们”，由机器人补齐空位。AI 需要账号；设置会显示当前可用的模式、人数与 Premium 要求。讨论语言支持英语、俄语、繁体中文、西班牙语和越南语，请按支持的语言选择，别把免费真人局的条件直接套用于 AI 对局。',
        ],
      },
      {
        title: '从选政府到选队伍：两种投票有什么不同？',
        paragraphs: [
          '两款游戏都要求多数人批准提案，平票则否决。留意谁支持过谁，仍然很有用。在阿瓦隆中，要问清楚一个人为什么支持整支队伍，不要只把队长当成唯一需要接受审视的人。',
          'Secret Hitler 的政策抽牌可能让忠于自由派的执政者也没有自由派政策可选。阿瓦隆基础版没有共用的政策牌堆：好人队员必须选择“成功”，坏人队员则可以选择“成功”或“失败”。因此，一张“失败”牌能透露队伍的情况，但无法指出究竟是谁出的。',
        ],
        source: secretHitlerRules,
      },
      {
        title: '任务成功为什么不能证明队员都是好人？',
        paragraphs: [
          '假设在五人对局中，爱丽丝和本参加第一次任务。三票赞成就足以让他们出发。基础配置下，如果任务失败，至少一人属于邪恶阵营；如果任务成功，两人中的任何一个仍可能是为了赢得信任而选择“成功”的坏人。',
          '把这个结果与他们之后的投票和组队提案进行比较。再次使用成功的队伍是需要讨论的决定，并不意味着这些玩家已被证实为好人。七人及以上对局的第四次任务需要两张“失败”牌才会失败；成功的结果中也可能出现一张“失败”。',
        ],
      },
      {
        title: '梅林与刺杀：阿瓦隆多了哪层身份推理？',
        paragraphs: [
          '在 Secret Hitler 中，某位邪恶玩家的身份是选举获胜条件的关键。阿瓦隆则让好人梅林在开局时获得有关邪恶阵营的信息。梅林必须引导好人，同时避免暴露自己掌握的信息，因为三次任务成功后，邪恶阵营仍有一次刺杀机会。',
          '不要直接沿用“好人应该公开所有信息”的习惯。自信的解释可能帮助任务成功，也可能帮助邪恶阵营认出梅林。角色指南会说明梅林能看到什么，以及哪些可选角色会改变这些信息。',
        ],
      },
      {
        title: '喜欢 Secret Hitler，什么时候值得换阿瓦隆？',
        paragraphs: [
          '如果你们想体验不同规模的任务队伍、从任务结果中推理，并让所有人持续参与，可以试试阿瓦隆。如果朋友最喜欢政策的不确定性和行政权力，继续玩 Secret Hitler 也很好。两款游戏都有浏览器版本，能用浏览器游玩并不是换游戏的唯一理由。',
          '在本站玩普通真人阿瓦隆时，请凑齐 5–10 人，让大家登录，并采用简单的可选角色和扩展配置。你也可以先用机器人学习。不要直接假设提案被否决或刺杀的规则与实体桌游完全一致，先阅读下方的平台差异。',
        ],
        source: { label: 'Secret Hitler.io 的规则与浏览器版本', url: 'https://secrethitler.io/rules' },
      },
      {
        title: '先用机器人熟悉任务牌，再带朋友开局',
        paragraphs: [
          '如果玩 Secret Hitler 的朋友还没上线，你可以先单人和机器人玩阿瓦隆。带引导的“首次游戏”帮助你学习组队投票与任务牌，不必先组织一整组人。观察任务成功如何建立信任，却不能证明某人一定是好人。',
          '机器人对局也让你在教朋友前，先了解梅林和终局刺杀。你也可以邀请朋友，让机器人补齐空位。在设置中选择对局人数和模式；这里会显示当前可用情况，并标注需要 Premium 的对局人数。',
        ],
      },
    ],
  },
  avalon_vs_werewolf: {
    slug: 'avalon-vs-werewolf',
    breadcrumb: '阿瓦隆与狼人杀对比',
    title: '阿瓦隆和狼人杀的区别：不想提前出局怎么选？',
    lead: '阿瓦隆靠组队投票与任务结果推理，任务期间无人被淘汰，每位玩家都能持续讨论。经典狼人杀靠昼夜行动与放逐投票，适合喜欢夜间技能和淘汰机制的玩家。',
    table: {
      caption: '经典狼人杀或 Mafia 与阿瓦隆基础版对比',
      columns: ['游戏环节', '经典狼人杀／Mafia', '阿瓦隆'],
      rows: [
        {
          title: '集体决策',
          cells: ['投票淘汰一名嫌疑人。', '批准或否决提议的任务队伍。'],
        },
        {
          title: '秘密行动',
          cells: ['攻击、查验等夜间行动，细节取决于所用规则。', '被选中的玩家提交“成功”或“失败”任务牌。'],
        },
        {
          title: '被怀疑之后',
          cells: ['被淘汰可能意味着无法继续主动参与。', '你仍能参与讨论和每一次组队投票。'],
        },
        {
          title: '不断积累的线索',
          cells: ['讨论、投票、夜间结果与角色信息。', '组队提案、公开的赞成或反对票与任务结果。'],
        },
        {
          title: '主持方式',
          cells: ['经典线下游戏通常需要主持人；线上工具可以处理行动。', '本平台会处理秘密角色、投票和任务结果。'],
        },
      ],
    },
    sections: [
      {
        title: '狼人杀朋友没到齐，可以先试一局吗？',
        paragraphs: [
          '可以先单人玩阿瓦隆，也可以邀请朋友，让机器人补齐空位，练习“谁上任务”而不是“谁被放逐”。AI 需要账号；进入设置确认模式、人数和当前可用情况，部分人数需要 Premium。机器人讨论支持英语、俄语、繁体中文、西班牙语和越南语。',
          '改开普通真人局时，仍需 5–10 位真人，每人用自己的账号和设备加入。本站在浏览器中免费提供普通真人对局，无需下载客户端；房主分享房间链接即可邀请朋友。先约好发言语言和聊天方式，组队公告板可帮助联系玩家，但不保证即时匹配。',
        ],
      },
      {
        title: '阿瓦隆不提前淘汰，被怀疑后还能做什么？',
        paragraphs: [
          '在经典狼人杀中，村民白天讨论嫌疑人，隐藏的敌人在夜间行动。被淘汰的玩家通常不再参与正式讨论和投票。Mafia 有许多地方规则，开局前应先约定版本，不要假设所有角色和胜利条件都一样。',
          '在阿瓦隆中，没被选进队伍并不意味着出局。这位玩家仍可质疑提案、投票，并成为下一位队长。即使是大家都不信任的玩家，也能影响结果，这改变了群体处理怀疑的方式。',
        ],
        source: werewolfRules,
      },
      {
        title: '从放逐投票到组队投票：五人局怎么判断？',
        paragraphs: [
          '不要只停留在“爱丽丝听起来像坏人”，还要问自己会批准哪支队伍，为什么。五人对局的第一次任务需要两人，五位玩家中至少三位必须赞成。只有选中的两人选择任务牌，但所有人都能分析结果。',
          '基础版任务中出现“失败”，说明至少一位队员属于邪恶阵营，但不会揭露是谁破坏了任务。“成功”也不能证明两人都是好人，因为坏人可以选择“成功”。应继续比较每个人提议的队伍和投票，不要凭一次结果就下定论。',
        ],
      },
      {
        title: '梅林和预言家的区别：开局信息与夜间查验',
        paragraphs: [
          '经典狼人杀的预言家通过夜间行动获得信息。阿瓦隆的梅林在开局时获得信息，但莫德雷德等角色属于例外。阿瓦隆基础版没有每夜重复的查验。梅林的挑战是帮助好人，同时不被认出。',
          '三次任务失败后，邪恶阵营获胜。三次任务成功后，邪恶阵营仍能尝试刺杀梅林。这是游戏的最终决策，不是先淘汰一人、再让其他人继续玩更多回合。',
        ],
      },
      {
        title: '一夜狼人也不提前淘汰，玩法有何不同？',
        paragraphs: [
          'One Night Ultimate Werewolf（一夜终极狼人）本身就没有提前淘汰，也不需要主持人。它包含一夜和随后的讨论，而不是经典狼人杀反复进行的昼夜循环。如果你们已经在玩一夜狼人，阿瓦隆带来的是连续的任务投票和结果；让所有人都能参与并不是阿瓦隆独有的特点。',
        ],
        source: oneNight,
      },
      {
        title: '想持续推理，还是喜欢夜间技能？',
        paragraphs: [
          '如果想持续讨论、保持固定的 5–10 人阵容，并有多次机会检验队伍，可以选择普通真人阿瓦隆。阿瓦隆的机器人对局也让你能单人或与朋友体验同样的任务决策。如果你们喜欢夜间能力、淘汰嫌疑人和不断变化的在场人数，可以选择经典狼人杀或 Mafia。狼人杀也有线上版本，包括这里链接的浏览器版本。',
          '转换游戏时，先教组队投票和任务牌，再解释梅林与刺杀。请使用我们规则中的人数和角色配置表，不要把 Mafia 的角色平衡配置直接套用到阿瓦隆。',
        ],
        source: browserWerewolf,
      },
      {
        title: '狼人杀玩家先用机器人练习组队投票',
        paragraphs: [
          '试试“单人”模式或带引导的“首次游戏”，把熟悉的淘汰投票换成任务队伍投票。机器人补齐座位，你则自己参与讨论并作出游戏决策。留意即使没被选进任务队伍，你仍然可以投票和观察结果。',
          '通过这局游戏了解梅林的开局信息和刺杀决策，再把所学带给狼人杀或 Mafia 的朋友。你也可以邀请朋友，让机器人补齐空位。在设置中选择对局人数、模式和支持的讨论语言，这里会显示当前可用情况。',
        ],
      },
    ],
  },
};

export const copy: GuideCopy = {
  tocTitle: '本文内容',
  comparisonLabel: '游戏对比',
  setupLabel: '参与条件与开局',
  aiAccessNote: '需要账号。语言、模式和 Premium 要求见设置。',
  home: '首页',
  breadcrumbLabel: '面包屑导航',
  setupTitle: '在这里玩阿瓦隆：和朋友或机器人一起',
  humanTitle: '和真人朋友进行普通对局',
  playersLabel: '玩家',
  humanPlayers: '5–10 位真人，每人使用自己的设备。',
  accessLabel: '参与条件',
  humanAccess: '免费的浏览器对局。每位玩家都需要账号。',
  discussionLabel: '讨论',
  humanDiscussion: '文字聊天，可选语音聊天（测试版）。',
  findingGroupLabel: '寻找队友',
  findingGroup: '与朋友约好时间，或使用玩家和组队公告板。',
  boardsNote: '公告板帮助大家协调组局，不保证即时匹配。',
  aiTitle: '单人尝试，或与朋友和机器人一起玩',
  aiIntro:
    '你可以单人游玩，也可以邀请朋友，让机器人补齐空位。在设置中选择对局人数和模式。刚接触阿瓦隆？试试带引导的“首次游戏”。',
  aiAvailability:
    '你需要账号。机器人讨论支持英语、俄语、繁体中文、西班牙语和越南语。在设置中选择模式；这里会显示当前可用情况，并标注需要 Premium 的对局人数。',
  startLabel: '开始玩阿瓦隆',
  aiCta: '试试机器人对局',
  lobbyCta: '打开阿瓦隆大厅',
  findGroupCta: '寻找队伍',
  rulesCta: '学习规则',
  sourceLabel: '来源',
  variantTitle: '第一次开局前，请确认这两条规则',
  proposalVariant:
    '原版阿瓦隆桌游中，连续五支队伍被否决，邪恶阵营就会获胜。在本站，连续四次提案被否决后，第五支队伍通常无需投票便会执行任务；情节卡可能影响队伍批准。',
  assassinationVariant:
    '原版游戏中，邪恶阵营讨论后，由刺客最终猜测谁是梅林。在本站的普通真人房间，刺杀由邪恶阵营共同处理。机器人对局也会先召开邪恶阵营议会，再作出刺杀决策。可选角色和自定义角色可能改变可选目标，请确认房间设置。',
  fullRulesCta: '阅读完整规则与人数配置表',
  previewAlt: '阿瓦隆游戏面板，显示八个玩家座位和任务结果',
  previewCaption: '展示任务结果的阿瓦隆面板，取自本站现有的游戏预览。',
  nextTitle: '先和机器人入门，或带朋友一起玩',
  nextAi:
    '想在邀请一整组朋友前先学会玩法？打开大厅中的“与 AI 对战”，查看可用模式，并选择“单人”或带引导的“首次游戏”。要邀请朋友，请选择“和好友们”，分享已准备好的房间链接，让他们在你开始前加入。',
  nextHuman:
    '请 5–10 位朋友创建账号，约定语言和交流方式，然后打开大厅并创建房间。把房间链接分享给大家。刚接触阿瓦隆？先教组队投票和任务牌，再加入可选角色。',
  arrangeLabel: '游玩或组织阿瓦隆对局',
  browseGroupsCta: '浏览队伍',
  findPlayersCta: '寻找玩家',
  merlinCta: '了解梅林',
  relatedLabel: '更多社交推理游戏指南',
  relatedTitle: '继续比较',
  ownership: '游戏名称归各自权利人所有。本对比不代表他们认可或推荐本平台。',
};
